import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { MapPin, Mail, Phone, Clock, CheckCircle2, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export default function ContactPage() {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Send to backend API
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          language: language
        }),
      });

      if (response.ok) {
        setShowConfirmation(true);
        setFormData({ name: '', email: '', message: '' });
      } else {
        const error = await response.json();
        toast.error(t('contact.form.error'));
        console.error('Error:', error);
      }
    } catch (error) {
      toast.error(t('contact.form.error'));
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackToHome = () => {
    setShowConfirmation(false);
    navigate('/');
  };

  // Show confirmation screen if form was submitted successfully
  if (showConfirmation) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-2xl w-full bg-card border-primary/20 shadow-2xl animate-scale-in">
          <CardContent className="p-12 text-center">
            <div className="inline-block p-6 bg-primary/10 rounded-full mb-6 animate-bounce">
              <CheckCircle2 className="w-16 h-16 text-primary" />
            </div>
            
            <h1 className="text-4xl font-bold text-foreground mb-4">
              {t('contact.confirmation.title')}
            </h1>
            
            <div className="space-y-4 text-lg text-muted-foreground mb-8">
              <p className="leading-relaxed">
                {t('contact.confirmation.message')}
              </p>
              <p className="leading-relaxed">
                {t('contact.confirmation.details')}
              </p>
              <p className="leading-relaxed">
                {t('contact.confirmation.urgent')}
              </p>
              <div className="flex items-center justify-center gap-2 text-primary font-semibold mt-6">
                <Mail className="w-5 h-5" />
                <p>{t('contact.confirmation.email')}</p>
              </div>
            </div>

            {/* Contact Information */}
            <div className="bg-secondary/50 rounded-lg p-6 mb-8">
              <h3 className="font-semibold text-foreground mb-4">{t('contact.info.title')}</h3>
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div className="flex items-center gap-3 justify-center md:justify-start">
                  <Phone className="w-4 h-4 text-primary flex-shrink-0" />
                  <a href="tel:+38163637353" className="hover:text-primary transition-colors">
                    +381 63 63 73 53
                  </a>
                </div>
                <div className="flex items-center gap-3 justify-center md:justify-start">
                  <Mail className="w-4 h-4 text-primary flex-shrink-0" />
                  <a href="mailto:markovic@prirodanmed.com" className="hover:text-primary transition-colors">
                    markovic@prirodanmed.com
                  </a>
                </div>
              </div>
            </div>

            <Button 
              onClick={handleBackToHome}
              size="lg"
              className="btn-honey"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              {t('contact.confirmation.backButton')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-primary to-[hsl(42,100%,65%)] text-white py-20">
        <div className="container-custom px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center animate-fade-in">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
              {t('contact.title')}
            </h1>
            <p className="text-lg text-white/90">
              {t('contact.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="section-padding">
        <div className="container-custom px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {/* Contact Form */}
            <Card className="bg-card border-border shadow-lg animate-slide-up">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold mb-6 text-foreground">
                  {t('contact.form.submit')}
                </h2>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-foreground">
                      {t('contact.form.name')}
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="border-border focus:border-primary"
                      placeholder="John Doe"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-foreground">
                      {t('contact.form.email')}
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="border-border focus:border-primary"
                      placeholder="john@example.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-foreground">
                      {t('contact.form.message')}
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      className="border-border focus:border-primary resize-none"
                      placeholder="Your message here..."
                    />
                  </div>
                  <Button 
                    type="submit" 
                    disabled={isSubmitting}
                    className="w-full btn-honey"
                    size="lg"
                  >
                    {isSubmitting ? 'Sending...' : t('contact.form.submit')}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Contact Information */}
            <div className="space-y-8 animate-slide-up" style={{ animationDelay: '200ms' }}>
              <Card className="bg-card border-border shadow-lg">
                <CardContent className="p-8">
                  <h2 className="text-2xl font-bold mb-6 text-foreground">
                    {t('contact.info.title')}
                  </h2>
                  <div className="space-y-6">
                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-primary/10 rounded-lg">
                        <MapPin className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground mb-1">
                          {t('contact.info.address')}
                        </h3>
                        <p className="text-muted-foreground">
                          Žikiće Jovanovića Španca 14<br />
                          18000 Niš, Serbia
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-primary/10 rounded-lg">
                        <Mail className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground mb-1">
                          {t('contact.info.email')}
                        </h3>
                        <a 
                          href="mailto:markovic@prirodanmed.com"
                          className="text-muted-foreground hover:text-primary transition-colors"
                        >
                          markovic@prirodanmed.com
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-primary/10 rounded-lg">
                        <Phone className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground mb-1">
                          {t('contact.info.phone')}
                        </h3>
                        <a 
                          href="tel:+38163637353"
                          className="text-muted-foreground hover:text-primary transition-colors"
                        >
                          +381 63 63 73 53
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-primary/10 rounded-lg">
                        <Clock className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground mb-1">
                          {t('contact.info.hours')}
                        </h3>
                        <div className="text-muted-foreground space-y-1">
                          <p>
                            <strong>{t('contact.info.monday')}:</strong> {t('contact.info.mondayTime')}
                          </p>
                          <p>
                            <strong>{t('contact.info.weekend')}:</strong> {t('contact.info.weekendTime')}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Map with Address */}
              <Card className="bg-card border-border shadow-lg overflow-hidden">
                <CardContent className="p-0">
                  <iframe
                    title="Marković Family Farm Location"
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2907.8!2d21.912!3d43.319!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4755b0cb8d8e1c51%3A0x8d4f7a6c8b5e2d1a!2s%C5%BDikice%20Jovanovi%C4%87a%20%C5%A0panca%2014%2C%20Ni%C5%A1%2C%20Serbia!5e0!3m2!1sen!2srs!4v1703421000000"
                    width="100%"
                    height="400"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    className="w-full"
                  ></iframe>
                  <div className="p-6 bg-secondary/50">
                    <div className="flex items-center gap-3">
                      <MapPin className="w-5 h-5 text-primary flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-foreground">{t('contact.info.address')}</p>
                        <p className="text-muted-foreground text-sm">
                          Žikiće Jovanovića Španca 14<br />
                          18000 Niš, Serbia
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}